import { Truck, ShieldCheck, FileBadge, Route, Phone, Mail, ArrowUpRight } from "lucide-react";

const LOGISTICS_BG =
  "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=2000&q=80";

const credentials = [
  { icon: FileBadge, label: "US DOT #", value: "4311432" },
  { icon: FileBadge, label: "MC #", value: "1680527" },
  { icon: ShieldCheck, label: "General Liability", value: "$1M / $2M combined" },
  { icon: ShieldCheck, label: "Cargo & Auto Insurance", value: "$1M limits" },
];

const partners = [
  { name: "3G Logistics", note: "Amazon Relay routes" },
  { name: "Remmy Logistics", note: "Contracted lanes" },
  { name: "USPS", note: "Mail transport contracts" },
  { name: "Currie App", note: "Load network partner" },
  { name: "Rhodie App", note: "Load network partner" },
  { name: "…and many more", note: "Additional partners on request" },
];

const services = [
  {
    icon: Truck,
    title: "Long-Haul Freight",
    desc: "Full truckload service across the continental United States with dedicated drivers and vetted equipment.",
  },
  {
    icon: Route,
    title: "Dedicated Route Contracts",
    desc: "Recurring lane commitments — including Amazon Relay, USPS, and enterprise brokerages — executed on time, every time.",
  },
  {
    icon: ShieldCheck,
    title: "Fully Compliant Carrier",
    desc: "DOT/MC authority in good standing with best-in-class general liability, cargo, and auto insurance coverage.",
  },
];

export const Logistics = () => {
  return (
    <section
      id="logistics"
      data-testid="logistics-section"
      className="relative bg-[#050914] border-t border-white/10 overflow-hidden"
    >
      {/* Hero band */}
      <div className="relative">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${LOGISTICS_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050914]/95 via-[#050914]/80 to-[#050914]/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050914]/40 via-transparent to-[#050914]" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <p
            data-testid="logistics-overline"
            className="text-[11px] font-bold uppercase tracking-[0.32em] text-[#d4af37] mb-6 reveal"
          >
            <span className="gold-rule" /> Logistics &amp; Transportation Division
          </p>
          <h2
            data-testid="logistics-headline"
            className="font-serif-editorial text-4xl md:text-5xl lg:text-[72px] font-light leading-[1.02] text-white max-w-3xl reveal"
          >
            Continental U.S. freight,
            <br />
            <span className="italic text-[#d4af37]">delivered with precision.</span>
          </h2>
          <p className="mt-8 max-w-2xl text-slate-300/90 leading-relaxed text-base md:text-lg reveal">
            Richmond Holdings Group Inc. operates a fully authorized motor carrier
            arm serving shippers, brokerages, and federal partners across the lower
            48. Our fleet moves everything from Amazon Relay loads to USPS mail
            contracts — on time, insured, and DOT-compliant.
          </p>
        </div>
      </div>

      {/* Services */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                data-testid={`logistics-service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="bg-[#0a1324] hover:bg-[#0f1a2f] transition-colors duration-500 p-8 md:p-10 reveal"
              >
                <Icon size={28} strokeWidth={1.2} className="text-[#d4af37] mb-6" />
                <h3 className="font-serif-editorial text-2xl text-white leading-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Credentials */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-[#d4af37] mb-5 reveal">
              <span className="gold-rule" /> Compliance &amp; Coverage
            </p>
            <h3 className="font-serif-editorial text-3xl md:text-4xl lg:text-5xl font-light text-white leading-[1.05] reveal">
              Fully authorized. <span className="italic text-[#d4af37]">Fully insured.</span>
            </h3>
            <p className="mt-6 text-slate-400 leading-relaxed max-w-md reveal">
              Every shipment moves under active federal authority and industry-leading
              insurance coverage — the credentials shippers require before the first mile.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {credentials.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.label}
                  data-testid={`logistics-cred-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="bg-[#0a1324] p-6 md:p-8 reveal"
                >
                  <Icon
                    size={24}
                    strokeWidth={1.2}
                    className="text-[#d4af37] mb-4"
                  />
                  <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                    {c.label}
                  </p>
                  <p className="font-serif-editorial text-xl md:text-2xl text-white mt-2 leading-tight">
                    {c.value}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Partners */}
        <div className="mt-20 md:mt-28">
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-[#d4af37] mb-5 reveal">
            <span className="gold-rule" /> Experience &amp; Partners
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <h3 className="lg:col-span-7 font-serif-editorial text-3xl md:text-4xl lg:text-5xl font-light text-white leading-[1.05] reveal">
              Trusted by the networks
              <br />
              that <span className="italic text-[#d4af37]">move America.</span>
            </h3>
            <p className="lg:col-span-5 text-slate-400 leading-relaxed reveal">
              Prior and current partnerships across e-commerce, federal mail,
              brokerage networks, and dedicated freight applications.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {partners.map((p) => (
              <div
                key={p.name}
                data-testid={`logistics-partner-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-[#0a1324] p-6 md:p-8 hover:bg-[#0f1a2f] transition-colors duration-500 reveal"
              >
                <p className="font-serif-editorial text-xl md:text-2xl text-white leading-tight">
                  {p.name}
                </p>
                <p className="mt-2 text-[11px] uppercase tracking-[0.24em] text-slate-500">
                  {p.note}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Logistics contact panel */}
        <div className="mt-20 md:mt-28 relative border border-[#d4af37]/40 bg-gradient-to-br from-[#0a1324] to-[#050914] p-8 md:p-14 reveal">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-[#d4af37] mb-5">
                <span className="gold-rule" /> Logistics Contact
              </p>
              <h3 className="font-serif-editorial text-3xl md:text-4xl lg:text-5xl font-light text-white leading-[1.05]">
                Have a load or a lane?
                <br />
                <span className="italic text-[#d4af37]">Let&apos;s move it.</span>
              </h3>
              <p className="mt-5 text-slate-400 max-w-lg">
                Reach our logistics desk directly for rate quotes, capacity checks,
                dedicated lanes, and carrier packet requests.
              </p>
            </div>
            <div className="lg:col-span-5 space-y-4">
              <a
                href="mailto:logistics@richmondhg.com"
                data-testid="logistics-email"
                className="group flex items-center justify-between gap-4 border border-white/10 hover:border-[#d4af37] bg-white/[0.02] hover:bg-white/[0.05] transition-colors p-5"
              >
                <div className="flex items-center gap-4">
                  <Mail size={22} className="text-[#d4af37]" strokeWidth={1.4} />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                      Email
                    </p>
                    <p className="font-serif-editorial text-lg md:text-xl text-white mt-1">
                      logistics@richmondhg.com
                    </p>
                  </div>
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-slate-600 group-hover:text-[#d4af37] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                />
              </a>
              <a
                href="tel:+17036410872"
                data-testid="logistics-phone"
                className="group flex items-center justify-between gap-4 border border-white/10 hover:border-[#d4af37] bg-white/[0.02] hover:bg-white/[0.05] transition-colors p-5"
              >
                <div className="flex items-center gap-4">
                  <Phone size={22} className="text-[#d4af37]" strokeWidth={1.4} />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                      Direct Line
                    </p>
                    <p className="font-serif-editorial text-lg md:text-xl text-white mt-1">
                      (703) 641-0872
                    </p>
                  </div>
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-slate-600 group-hover:text-[#d4af37] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Logistics;
